import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { isNil } from '@flex-design-system/fx-utils';
import { styled } from '@flex-design-system/stitches-react';
import { Primitive } from '@radix-ui/react-primitive';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import classNames from 'classnames';
import { forwardRef, useMemo } from 'react';
import { TagSlot } from './components/slot/Slot.mjs';
import { TagText } from './components/text/Text.mjs';
import { TagContextProvider } from './context.mjs';
import { tagHorizontalGap, tagBackground, tagColor, tag, tagVariants } from './tag.vanilla.mjs';
import { getTagColor } from './utils/color.mjs';

const TagRoot = forwardRef(
  ({
    children,
    className: classNameFromProps,
    style: styleFromProps,
    color = "gray",
    size = "default",
    space = 2,
    variant = "solid",
    leftSlot,
    rightSlot,
    ...props
  }, forwardedRef) => {
    const { color: restrictedTagColor, background: restrictedTagBackground } = getTagColor({ color, variant });
    const style = Object.assign(
      {},
      assignInlineVars({
        [tagColor]: restrictedTagColor,
        [tagBackground]: restrictedTagBackground,
        [tagHorizontalGap]: `${space}px`
      }),
      styleFromProps
    );
    const className = classNames(
      tag,
      tagVariants({ size, variant }),
      classNameFromProps
    );
    const child = useMemo(() => {
      const isObjectTypeChildren = typeof children === "object";
      return isNil(leftSlot) && isNil(rightSlot) ? children : /* @__PURE__ */ jsxs(Fragment, { children: [
        !isNil(leftSlot) && /* @__PURE__ */ jsx(TagSlot, { children: leftSlot }),
        isObjectTypeChildren ? children : /* @__PURE__ */ jsx("div", { "data-scope": "tag", "data-part": "content", children }),
        !isNil(rightSlot) && /* @__PURE__ */ jsx(TagSlot, { children: rightSlot })
      ] });
    }, [children, leftSlot, rightSlot]);
    return /* @__PURE__ */ jsx(TagContextProvider, { size, children: /* @__PURE__ */ jsx(
      StyledDiv,
      {
        "data-scope": "tag",
        "data-part": "root",
        className,
        style,
        ref: forwardedRef,
        ...props,
        children: child
      }
    ) });
  }
);
const StyledDiv = styled(Primitive.div);
const Tag = Object.assign({}, TagRoot, {
  Text: styled(TagText),
  /**
   * @deprecated
   * Tag.Slot 컴포넌트를 사용해 주세요.
   */
  LeftSlotRoot: TagSlot,
  /**
   * @deprecated
   * Tag.Slot 컴포넌트를 사용해 주세요.
   */
  RightSlotRoot: TagSlot,
  Slot: TagSlot
});
Tag.displayName = "Tag";

export { Tag };
//# sourceMappingURL=Tag.mjs.map
